import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import PageHero from '../components/PageHero';
import { Check, FileText, ExternalLink, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { EditRegion } from '../components/AdminEditBar';
import { InlineText } from '../components/InlineEditable';
import { api } from '../lib/api';

const DEFAULT_DOCS = [
  { id: 'tax-main', title: 'Kamu Yararı Kararı Belgesi', file: '#', description: 'PDF olarak indir' },
];

export default function TaxExemption() {
  useSEO('tax');
  const [docs, setDocs] = useState(DEFAULT_DOCS);

  useEffect(() => {
    api.publicContent('tax_docs').then(list => {
      if (Array.isArray(list) && list.length > 0) {
        setDocs(list.map(it => ({ id: it.id, ...(it.data || {}) })));
      }
    }).catch(() => {});
  }, []);

  return (
    <EditRegion label="Vergi Muafiyeti" to="/admin/tax-docs">
      <>
      <PageHero slug="tax"
        kicker="Kamu Yararına Vergi Muafiyeti"
        title={<>Bağışlarınız için <span className="italic text-coral-200">vergi avantajı</span>.</>}
        text="YEKÜV, Bakanlar Kurulu kararı ile Kamu Yararına Çalışan Vakıflar statüsündedir."
      />
      <section className="bg-cream">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-20 md:py-28 space-y-8">
          {/* Statü ve mevzuat card */}
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100">
            <InlineText settingKey="tax_status_title" fallback="Statü ve mevzuat" as="h2"
              className="font-display text-3xl md:text-4xl text-ink-600" />
            <InlineText settingKey="tax_status_body"
              fallback="YEKÜV, 1992 yılındaki kuruluşundan kısa süre sonra Bakanlar Kurulu kararı ile Kamu Yararına Çalışan Vakıflar arasına alınmıştır. Bu statüden dolayı, vakfımıza yapılan nakdî ve aynî bağışlar ilgili mevzuat çerçevesinde vergi indirimi avantajı sağlar."
              as="p" multiline className="mt-4 text-ink-500 leading-relaxed" />
          </div>

          {/* Bireysel bağışçılar için */}
          <div className="bg-ink-600 text-cream rounded-3xl p-8 md:p-10">
            <InlineText settingKey="tax_individual_title" fallback="Bireysel bağışçılar için" as="h3"
              className="font-display text-2xl md:text-3xl" />
            <ul className="mt-4 space-y-3 text-cream/75 text-sm">
              {['tax_individual_1', 'tax_individual_2', 'tax_individual_3'].map((key, i) => {
                const fallbacks = [
                  "Yıllık gelirinizin %5'ine kadar yapılan bağışlar gelir vergisi matrahından indirilebilir.",
                  'Bağış makbuzlarımız için e-posta veya posta ile gönderim yapılır.',
                  'Bağış makbuzunuzu yıllık gelir vergisi beyannamenize ekleyebilirsiniz.',
                ];
                return (
                  <li key={key} className="flex gap-2">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <InlineText settingKey={key} fallback={fallbacks[i]} as="span" multiline className="flex-1" />
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Kurumlar için */}
          <div className="bg-white rounded-3xl p-8 md:p-10 border border-ink-100">
            <InlineText settingKey="tax_corporate_title" fallback="Kurumlar için" as="h3"
              className="font-display text-2xl md:text-3xl text-ink-600" />
            <ul className="mt-4 space-y-3 text-ink-500 text-sm">
              {['tax_corporate_1', 'tax_corporate_2', 'tax_corporate_3'].map((key, i) => {
                const fallbacks = [
                  "Yıllık kurum kazancının %5'ine kadar yapılan bağışlar indirim konusu yapılabilir.",
                  'Kurumsal bağışlar için özel raporlama sağlanabilir.',
                  'Sponsorluk sözleşmeleri ile uzun vadeli işbirlikleri yapılabilir.',
                ];
                return (
                  <li key={key} className="flex gap-2">
                    <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    <InlineText settingKey={key} fallback={fallbacks[i]} as="span" multiline className="flex-1" />
                  </li>
                );
              })}
            </ul>
          </div>

          {/* PDF documents list — admin add/manage via /admin/tax-docs */}
          <div className="space-y-3" data-testid="tax-docs-list">
            {docs.map((d, i) => (
              <EditRegion key={d.id || i} label="Belgeyi Düzenle" to="/admin/tax-docs">
                <a
                  href={d.file || '#'}
                  target={d.file && d.file !== '#' ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className="block bg-gold/20 border border-gold/40 rounded-3xl p-6 hover:bg-gold/30 transition-colors"
                  data-testid={`tax-doc-${d.id || i}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gold text-ink-700 grid place-items-center shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-xl text-ink-600 truncate">{d.title || 'İsimsiz belge'}</div>
                      <div className="text-sm text-ink-500">{d.description || (d.file && d.file !== '#' ? 'PDF olarak aç' : 'Henüz PDF eklenmedi')}</div>
                    </div>
                    {d.file && d.file !== '#' && (
                      <ExternalLink className="w-4 h-4 text-ink-500 shrink-0" />
                    )}
                  </div>
                </a>
              </EditRegion>
            ))}
          </div>

          {/* Admin add-doc CTA — visible only in edit mode */}
          <div className="yekuv-admin-only" style={{ display: 'none' }}>
            <Link
              to="/admin/tax-docs"
              data-testid="add-tax-doc"
              className="inline-flex items-center gap-2 bg-ink-600 text-cream rounded-full px-6 h-12 text-sm font-semibold hover:bg-ink-500"
            >
              <Plus className="w-4 h-4" /> Yeni PDF Belgesi Ekle
            </Link>
          </div>
        </div>
      </section>
    </>
    </EditRegion>
  );
}
